// Libs
import Vue from 'vue';
import Router from 'vue-router';
import store from '../store/index';
// Components
import dashboard from '@/components/dashboard';
import welcome from '@/components/welcome';
import login from '@/components/login';
import accountsList from '@/components/accountsList';
import home from '@/components/home';
import tfaAppSetup from '@/components/2fa/setup/tfaAppSetup';
import tfaCheck from '@/components/2fa/tfaCheck';
import changePassword from '@/components/changePassword';
import settings from '@/components/settings';
import settingsGeneral from '@/components/settings/settingsGeneral';
import settingsAuthorization from '@/components/settings/settingsAuthorization';
import sidebar from '@/components/sidebar';
import topbar from '@/components/topbar';
import queryErrorContainer from '@/components/queryErrorContainer';
import appearance from '@/components/settings/appearance/appearance';
import settingsLibraryDocs from '@/components/settings/settingsLibraryDocs';
import settingsApiDocs from '@/components/settings/settingsApiDocs';
import messenger from '@/components/settings/settingsMessenger';
import statistics from '@/components/statistics';
import users from '@/components/users';
import profile from '@/components/profile';
import settingsPush from '@/components/settings/settingsPush';
import settingsOfficeHours from '@/components/settings/settingsOfficeHours';
import settingsSpecialMessages from '@/components/settings/settingsSpecialMessages';
import settingsDataRetention from '@/components/settings/settingsDataRetention';
import settingsService from '@/components/settings/settingsService';
import template from '@/components/TEMPLATE'; // Only used in development mode
// Custom
import Auth from '@/util/auth';
import QueryErrors from '@/util/queryErrors';
import { eventBus } from '../main';

Vue.use(Router);

const lastPath = store.getters[store.types.path.getters.get].lastPath; // For the wildcard redirect
const router = new Router({
    mode: 'history',
    routes: [
        {
            path: '/',
            components: {
                queryErrorContainer: queryErrorContainer,
                dashboard: dashboard
            },
            props: {
                queryErrorContainer: true // RouterViewName: true, means that vue will convert 'params' to 'props'
            },
            children: [
                {
                    path: '/',
                    components: {
                        content: welcome
                    },
                    props: {
                        content: true
                    },
                    redirect: (lastPath.name && lastPath.params) ? {name: lastPath.name, params: lastPath.params} : {name: 'login', params: {}},
                    children: [
                        {
                            path: 'login',
                            name: 'login',
                            components: {
                                default: login
                            },
                            props: {
                                default: true, // This is needed to 'enable' props on the component
                                content: true
                            },
                            meta: {
                                dontRememberMe: true
                            }
                        },
                        {
                            path: '2fa/check',
                            name: 'tfaCheck',
                            components: {
                                default: tfaCheck
                            },
                            props: {
                                default: true,
                                content: true
                            },
                            meta: {
                                requiresTempToken: true,
                                dontRememberMe: true
                            }
                        },
                        {
                            path: 'password',
                            name: 'changePassword',
                            components: {
                                default: changePassword
                            },
                            props: {
                                default: true,
                                content: true
                            },
                            meta: {
                                requiresTempToken: true,
                                dontRememberMe: true
                            }
                        }
                    ]
                },
                {
                    path: '2fa/setup',
                    name: 'tfaAppSetup',
                    components: {
                        content: tfaAppSetup
                    },
                    meta: {
                        requiresTempToken: true,
                        dontRememberMe: true
                    }
                },
                {
                    path: 'accounts/list',
                    name: 'accountsList',
                    components: {
                        topbar: topbar,
                        sidebar: sidebar,
                        content: accountsList
                    },
                    props: {
                        content: true,
                        topbar: {title: 'Accounts List'}
                    },
                    meta: {
                        requiresToken: true
                    }
                },
                {
                    path: 'account/:identification',
                    name: 'home',
                    components: {
                        sidebar: sidebar,
                        content: home
                    },
                    meta: {
                        requiresToken: true,
                        requiresIdentification: true
                    }
                },
                {
                    path: '/account/:identification/settings',
                    name: 'settings',
                    components: {
                        topbar: topbar,
                        sidebar: sidebar,
                        content: settings
                    },
                    props: {topbar: {title: 'Settings'}},
                    children: [
                        {path: 'general', name: 'settingsGeneral', component: settingsGeneral},
                        {
                            path: 'appearance',
                            name: 'appearance',
                            component: appearance,
                            meta: {
                                requiresAnyRight: [
                                    Auth.getRightsList().UpdateDesignSettings
                                ]
                            }
                        },
                        {
                            path: 'libraryDocs',
                            name: 'settingsLibraryDocs',
                            component: settingsLibraryDocs,
                            beforeEnter() { window.open('https://developers.parley.nu/docs', '_blank'); }, // Open library docs extern, we don't use the component for now
                            meta: {
                                dontRememberMe: true
                            }
                        },
                        {path: 'apiDocs', name: 'settingsApiDocs', component: settingsApiDocs},
                        {
                            path: 'messenger',
                            name: 'messenger',
                            component: messenger,
                            meta: {
                                requiresAnyRight: [
                                    Auth.getRightsList().UpdateDesignSettings
                                ]
                            }
                        },
                        {
                            path: 'demoPage',
                            name: 'settingsDemoPage',
                            beforeEnter: to => {
                                const {params} = to;

                                window.open('https://parleycdn.com/demo/advanced.html?accountId=' + params.identification + '&background=https://parley.nu/images/tab6.png&start=true', '_blank');
                                return {name: 'settings'};
                            },
                            meta: {
                                dontRememberMe: true
                            }
                        },
                        {
                            path: 'authorization',
                            name: 'settingsAuthorization',
                            component: settingsAuthorization,
                            meta: {
                                requiresAnyRight: [
                                    Auth.getRightsList().GetSharedSecret,
                                    Auth.getRightsList().AuthorizationHeadersSettings,
                                    Auth.getRightsList().AuthorizationHeaderGenerateAndValidate
                                ]
                            }
                        },
                        {
                            path: 'service',
                            name: 'settingsService',
                            component: settingsService,
                            meta: {
                                requiresAnyRight: [
                                    Auth.getRightsList().ManageServices
                                ]
                            }
                        },
                        {
                            path: 'data-retention',
                            name: 'settingsDataRetention',
                            component: settingsDataRetention,
                            meta: {
                                requiresAnyRight: [
                                    // TODO
                                ]
                            }
                        },
                        {
                            path: 'push',
                            name: 'settingsPush',
                            component: settingsPush,
                            meta: {
                                requiresAnyRight: [
                                    Auth.getRightsList().iOSPushSettings,
                                    Auth.getRightsList().AndroidPushSettings,
                                    Auth.getRightsList().WebPushSettings
                                ]
                            }
                        },
                        {
                            path: 'officeHours',
                            name: 'settingsOfficeHours',
                            component: settingsOfficeHours,
                            meta: {
                                requiresAnyRight: [
                                    Auth.getRightsList().ManageOfficeHours
                                ]
                            }
                        },
                        {
                            path: 'specialMessages',
                            name: 'settingsSpecialMessages',
                            component: settingsSpecialMessages,
                            meta: {
                                requiresAnyRight: [
                                    Auth.getRightsList().ManageOfficeHours
                                ]
                            }
                        }
                    ],
                    meta: {
                        requiresToken: true,
                        requiresIdentification: true,
                        dontRememberMe: true
                    }
                },
                {
                    path: '/account/:identification/statistics',
                    name: 'statistics',
                    components: {
                        topbar: topbar,
                        sidebar: sidebar,
                        content: statistics
                    },
                    props: {topbar: {title: 'Statistics'}},
                    meta: {
                        requiresToken: true,
                        requiresIdentification: true
                    }
                },
                {
                    path: '/account/:identification/users',
                    name: 'users',
                    components: {
                        topbar: topbar,
                        sidebar: sidebar,
                        content: users
                    },
                    props: {topbar: {title: 'Users'}},
                    meta: {
                        requiresToken: true,
                        requiresIdentification: true,
                        requiresAnyRight: [
                            Auth.getRightsList().SearchUsersInAccount
                        ]
                    }
                },
                {
                    path: 'account/:identification/*',
                    redirect: to => {
                        const {params} = to;

                        return { name: 'home', params: params };
                    }
                },
                {
                    path: 'profile',
                    name: 'profile',
                    components: {
                        topbar: topbar,
                        sidebar: sidebar,
                        content: profile
                    },
                    props: {topbar: {title: 'Profile'}},
                    meta: {
                        requiresToken: true
                    }
                }
            ]
        },
        { // This one needs to be last!
            path: '*',
            redirect: {name: 'login', params: {}} // Empty params is hotfix for error: domexception setattr 0
        }
    ]
});

if (process.env.NODE_ENV === 'development')
    router.addRoutes([{
        path: '/',
        components: {
            queryErrorContainer: queryErrorContainer,
            dashboard: dashboard
        },
        props: {
            queryErrorContainer: true // RouterViewName: true, means that vue will convert 'params' to 'props'
        },
        children: [
            {
                path: 'template',
                name: 'template',
                components: {
                    topbar: topbar,
                    sidebar: sidebar,
                    content: template
                },
                props: {
                    topbar: {title: 'TEMPLATE'}
                }
            }
        ]
    }]);

// Do this before going to any route
router.beforeEach(async (to, from, next) => {
    // Clear any errors from the screen
    // - exclude accountsList call to get accounts, because when you try to skip accountsList by navigating to
    // - /account/IDENTIFICATION, you will get send back to the accountsList with the message that you need to log
    // - in to an account. This message gets deleted without this if-statement
    if (to.name !== 'accountsList')
        QueryErrors.clearErrors();

    // Save callback and other params into store
    if (to.params) {
        let newPathObj = {};
        if (to.params.callback) newPathObj.callback = to.params.callback;
        if (to.params.setup) newPathObj.setup = to.params.setup;
        if (to.params.queryErrors) newPathObj.queryErrors = to.params.queryErrors;

        await store.dispatch(store.types.path.actions.set, newPathObj);
    }

    // Route checks
    if (to.matched.some(record => record.meta.requiresToken)) { // Route requires a valid token
        if (!Auth.requireToken()) // Token is invalid (user is not logged in)
            eventBus.$emit('showSessionExpireModal');
    } else if (to.name === 'login' && Auth.requireToken()) // Route requires no valid token and user wants to go to login while already logged in
        return next({name: 'accountsList', params: {callback: to.path}}); // Redirect to accountsList
    if (to.matched.some(record => record.meta.requiresIdentification)) // Route requires an identification
        if (!Auth.requireIdentification() || to.params.identification !== store.getters[store.types.loggedinAccount.getters.get].identification) // No identification present (user not logged into an account) OR Current identification doesn't match identification from params ('from' doesn't match 'to')
            return tryLogUserIntoAccount(to.name, to.params, next, {name: 'accountsList', params: {queryErrors: ['identification-error'], callback: to.path}});// Try to log the user into that account so we can skip accounts/list
    if (to.matched.some(record => record.meta.requiresTempToken)) // Route requires a temp token
        if (!Auth.requireTempToken()) // No temp token present (user not logged in, or completely logged in)
            if (Auth.requireToken()) // User is completely logged in
                return next({name: 'accountsList', params: {callback: to.path}}); // Redirect to accountsList
    if (to.name === 'tfaCheck' && !store.getters[store.types.user.getters.get].activated) // Goto 2fa setup when we are not activated and going to tfaCheck
        return next({name: 'tfaAppSetup'});
    if (!store.getters[store.types.user.getters.get].superUser) // Superusers have access to everything, so skip 'right check'
        if (to.matched.some(record => record.meta.requiresAnyRight && record.meta.requiresAnyRight.length > 0)) { // Check if we have the needed rights to access the page
            const rights = to.matched.filter(record => record.meta.requiresAnyRight && record.meta.requiresAnyRight.length > 0)[0].meta.requiresAnyRight;
            const userRights = store.getters[store.types.user.getters.get].rights;
            let hasAccess = false;
            rights.forEach(right => {
                if (userRights.indexOf(right.id) > -1)
                    hasAccess = true;
            });

            if (!hasAccess)
                return next({name: 'accountsList', params: {callback: to.path}}); // Redirect to accountsList TODO: Is this the best redirection?
        }

    // Reset callback when we have reached the callback
    if (to.path === store.getters[store.types.path.getters.get].callback)
        await store.dispatch(store.types.path.actions.set, {callback: null});

    // Keep track of latest path
    // Ignore paths with the meta tag 'dontRememberMe: true'
    if (to.name && !to.meta.dontRememberMe)
        await store.dispatch(store.types.path.actions.set, {lastPath: {fullPath: to.fullPath, name: to.name, params: to.params}});

    return next(); // Redirection
});

async function tryLogUserIntoAccount(toRouteName, toParams, next, nextObj) {
    async function loginFailed() {
        nextObj.params.callback = null;
        await store.dispatch(store.types.path.actions.set, {callback: null});
        return next(nextObj);
    }

    async function loginSuccess(account) {
        await router.app.$store.dispatch(router.app.$store.types.loggedinAccount.actions.set, account);
        // Get and Save Rights
        await router.app.$http.get('v1.1/rights').then(async response => {
            // Success callback
            await router.app.$store.dispatch(router.app.$store.types.user.actions.set, {rights: response.data.data.rights});
        }, response => {
            // Error callback
            QueryErrors.addError('Something went wrong while fetching the list of your permissions within this account. If you experience any problems try to log out and back in.');
        });
        return next({name: toRouteName, params: toParams});
    }

    // Do GET /accounts
    const result = await router.app.$http.get('v1.1/accounts').catch(() => { return next(nextObj); });

    if (!result || !result.body.data || !result.body.data.accounts || !result.body.data.accounts.length === 0) // Login failed (no accounts found)
        return loginFailed();

    const accounts = result.body.data.accounts;

    await router.app.$store.dispatch(router.app.$store.types.user.actions.hasAccessToMultipleAccounts, accounts.length > 1); // Save hasAccessToMultipleAccounts to vuex
    const matches = accounts.filter(account => {
        return account.identification === toParams.identification;
    });

    // Login success
    if (matches && matches.length > 0) // Should not be greater than 0, but we don't really care if it does
        return loginSuccess(matches[0]);

    // Login failed, navigate to nextObj
    return loginFailed();
}

export default router;
